// AI Chat Component - Angular chat widget with Arabic/English voice support

import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewChecked, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { trigger, transition, style, animate } from '@angular/animations';
import { AiChatService, ChatMessage } from './ai-chat.service';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-ai-chat',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './ai-chat.component.html',
    styleUrls: ['./ai-chat.component.scss'],
    animations: [
        trigger('fadeSlide', [
            transition(':enter', [
                style({ opacity: 0, transform: 'translateY(20px)' }),
                animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
            ]),
            transition(':leave', [
                animate('200ms ease-in', style({ opacity: 0, transform: 'translateY(20px)' }))
            ])
        ])
    ]
})
export class AiChatComponent implements OnInit, OnDestroy, AfterViewChecked {
    @ViewChild('messagesContainer') messagesContainer!: ElementRef;

    isOpen = false;
    inputMessage = '';
    messages: ChatMessage[] = [];
    isTyping = false;
    isConnected = false;
    language = 'en';

    // Voice chat
    isRecording = false;
    isProcessingVoice = false;
    private mediaRecorder: MediaRecorder | null = null;
    private audioChunks: Blob[] = [];
    private recordingMimeType = 'audio/webm';
    private recordingStartTime = 0;

    private subscriptions: Subscription[] = [];
    private shouldScrollToBottom = false;
    private translate = inject(TranslateService);

    // Language-specific quick questions
    private quickQuestionsMap: Record<string, string[]> = {
        en: [
            'How do I clock in?',
            'What is my leave balance?',
            'How to request leave?',
            'View my payslip',
        ],
        ar: [
            'كيف أسجل الحضور؟',
            'ما هو رصيد إجازاتي؟',
            'كيف أطلب إجازة؟',
            'عرض كشف الراتب',
        ],
    };

    // Language-specific UI text
    get uiText() {
        return this.language === 'ar' ? {
            title: 'مساعد إجازاتي',
            online: 'متصل',
            connecting: 'جاري الاتصال...',
            welcomeGreeting: 'مرحباً! أنا مساعدك في الموارد البشرية',
            welcomeDescription: 'اسألني أي شيء عن الحضور، الإجازات، الرواتب، والمزيد.',
            quickLabel: 'أسئلة سريعة:',
            inputPlaceholder: 'اسأل عن الموارد البشرية...',
            listeningPlaceholder: 'جاري الاستماع...',
            newConversation: 'محادثة جديدة',
            close: 'إغلاق',
            voiceChat: 'محادثة صوتية',
            recordingTooShort: 'التسجيل قصير جداً. يرجى الضغط والتحدث ثم تحرير الزر.',
            captureError: 'لم يتم التقاط الصوت. يرجى المحاولة مرة أخرى.',
            micError: 'لم أتمكن من الوصول إلى الميكروفون. يرجى التحقق من الأذونات.',
            voiceFailed: 'فشلت الرسالة الصوتية',
            processingVoice: '🎤 جاري التسجيل... (معالجة)',
        } : {
            title: 'Ejazaaty Assistant',
            online: 'Online',
            connecting: 'Connecting...',
            welcomeGreeting: "Hello! I'm your HR Assistant",
            welcomeDescription: 'Ask me anything about attendance, leave, payroll, and more.',
            quickLabel: 'Quick questions:',
            inputPlaceholder: 'Ask about HR...',
            listeningPlaceholder: 'Listening...',
            newConversation: 'New conversation',
            close: 'Close',
            voiceChat: 'Voice Chat',
            recordingTooShort: 'Recording was too short. Please hold the button and speak, then release.',
            captureError: 'Could not capture audio. Please try again.',
            micError: 'I could not access your microphone. Please check permissions.',
            voiceFailed: 'Voice message failed',
            processingVoice: '🎤 Recording... (processing)',
        };
    }

    get quickQuestions(): string[] {
        return this.quickQuestionsMap[this.language] || this.quickQuestionsMap['en'];
    }

    get isRtl(): boolean {
        return this.language === 'ar';
    }

    constructor(private chatService: AiChatService) { }

    ngOnInit(): void {
        // Detect current website language
        this.language = this.translate.currentLang || this.translate.defaultLang || 'en';

        // Listen for language changes across the website
        this.subscriptions.push(
            this.translate.onLangChange.subscribe(event => {
                this.language = event.lang;
            })
        );

        // Subscribe to messages
        this.subscriptions.push(
            this.chatService.messages$.subscribe(msgs => {
                this.messages = msgs;
                this.shouldScrollToBottom = true;
            })
        );

        // Subscribe to typing
        this.subscriptions.push(
            this.chatService.typing$.subscribe(typing => {
                this.isTyping = typing;
            })
        );

        // Subscribe to connection status
        this.subscriptions.push(
            this.chatService.connected$.subscribe(connected => {
                this.isConnected = connected;
            })
        );
    }

    ngAfterViewChecked(): void {
        if (this.shouldScrollToBottom) {
            this.scrollToBottom();
            this.shouldScrollToBottom = false;
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.chatService.disconnect();
    }

    toggleChat(): void {
        this.isOpen = !this.isOpen;
        if (this.isOpen && !this.isConnected) {
            this.chatService.connect();
        }
    }

    sendMessage(): void {
        if (!this.inputMessage.trim() || this.isTyping) return;

        this.chatService.sendMessage(this.inputMessage.trim(), this.language);
        this.inputMessage = '';
    }

    sendQuickQuestion(question: string): void {
        this.inputMessage = question;
        this.sendMessage();
    }

    newConversation(): void {
        this.chatService.newConversation();
    }

    async toggleRecording(): Promise<void> {
        if (this.isProcessingVoice) return; // Prevent double-clicks while processing
        if (this.isRecording) {
            this.stopRecording();
        } else {
            await this.startRecording();
        }
    }

    private async startRecording(): Promise<void> {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    channelCount: 1,
                    sampleRate: 16000,
                    echoCancellation: true,
                    noiseSuppression: true,
                }
            });

            // Determine the best supported MIME type
            const mimeTypes = [
                'audio/webm;codecs=opus',
                'audio/webm',
                'audio/ogg;codecs=opus',
                'audio/mp4',
                'audio/wav',
            ];
            this.recordingMimeType = 'audio/webm'; // default
            for (const mimeType of mimeTypes) {
                if (MediaRecorder.isTypeSupported(mimeType)) {
                    this.recordingMimeType = mimeType;
                    break;
                }
            }

            this.mediaRecorder = new MediaRecorder(stream, {
                mimeType: this.recordingMimeType,
            });
            this.audioChunks = [];

            this.mediaRecorder.ondataavailable = (event) => {
                if (event.data && event.data.size > 0) {
                    this.audioChunks.push(event.data);
                }
            };

            this.mediaRecorder.onstop = () => {
                // Stop all tracks first
                stream.getTracks().forEach(track => track.stop());

                // Check minimum recording duration (at least 500ms)
                const recordingDuration = Date.now() - this.recordingStartTime;
                if (recordingDuration < 500 || this.audioChunks.length === 0) {
                    console.warn('Recording too short or empty, skipping');
                    this.messages.push({
                        role: 'assistant',
                        content: this.uiText.recordingTooShort,
                        timestamp: new Date()
                    });
                    this.messagesSubject_emit();
                    return;
                }

                const audioBlob = new Blob(this.audioChunks, { type: this.recordingMimeType });
                console.log(`Voice recording: ${audioBlob.size} bytes, type: ${this.recordingMimeType}, duration: ${recordingDuration}ms, language: ${this.language}`);

                if (audioBlob.size > 0) {
                    this.sendVoiceMessage(audioBlob);
                } else {
                    console.error('Empty audio blob');
                    this.messages.push({
                        role: 'assistant',
                        content: this.uiText.captureError,
                        timestamp: new Date()
                    });
                }
            };

            // Start recording with timeslice (collect data every 250ms)
            this.mediaRecorder.start(250);
            this.isRecording = true;
            this.recordingStartTime = Date.now();
        } catch (error) {
            console.error('Error accessing microphone:', error);
            this.messages.push({
                role: 'assistant',
                content: this.uiText.micError,
                timestamp: new Date()
            });
        }
    }

    // Helper to emit messages since we push directly sometimes
    private messagesSubject_emit(): void {
        // Messages array is the same reference, just trigger change detection
        this.shouldScrollToBottom = true;
    }

    private stopRecording(): void {
        if (this.mediaRecorder && this.isRecording) {
            this.mediaRecorder.stop();
            this.isRecording = false;
        }
    }

    private sendVoiceMessage(blob: Blob): void {
        this.isProcessingVoice = true;
        this.chatService.sendVoiceMessage(blob, this.language).subscribe({
            next: (result) => {
                this.isProcessingVoice = false;
                // Play audio response
                if (result.audio && result.audio.size > 0) {
                    this.playBlob(result.audio);
                } else {
                    console.warn('Empty audio response received');
                }
            },
            error: (error) => {
                this.isProcessingVoice = false;
                console.error('Voice message failed:', error);
            }
        });
    }

    private playBlob(blob: Blob): void {
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);
        audio.play().catch(e => console.error('Audio playback failed:', e));
        audio.onended = () => {
            URL.revokeObjectURL(url);
        };
    }

    private scrollToBottom(): void {
        if (this.messagesContainer?.nativeElement) {
            const el = this.messagesContainer.nativeElement;
            el.scrollTop = el.scrollHeight;
        }
    }

    // Format message content (support markdown-like formatting)
    formatMessage(content: string): string {
        // Bold: **text**
        content = content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        // Line breaks
        content = content.replace(/\n/g, '<br>');
        // Lists: - item
        content = content.replace(/^- (.*)$/gm, '<li>$1</li>');
        // Numbers: 1. item  
        content = content.replace(/^\d+\. (.*)$/gm, '<li>$1</li>');

        return content;
    }
}
